import { useSessionStorage } from '@cheqroom/hooks';
import { QueryStatus, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useDoClearSession } from '../../api/clear-session.api';
import {
	DiscoveryResponse,
	getWorkspaceStrategies,
	resolveDiscoveryWorkspaces,
	useDoDiscovery,
	Workspace,
} from '../../api/discovery.api';
import { Step } from '../../App';
import { useReturnToOrganisationPicker } from '../SelectOrganisationForLoginStep/api.hooks';

type LoadDiscoveryResult = {
	status: QueryStatus;
	email?: string;
	/** Discovery workspace that matches the URL `workspaceId`. Undefined when the identity cookie belongs to another email. */
	workspace?: Workspace;
	onGoBack: () => void;
	/** Clears the identity cookie, then navigates to the email step. */
	clearSessionAndGoToEmail: () => void;
};

export const useLoadDiscovery = (queryOptions?: UseQueryOptions<DiscoveryResponse>): LoadDiscoveryResult => {
	const navigateTo = useNavigate();

	const { mutate: clearSession } = useDoClearSession({
		onSettled: () => navigateTo(Step.ENTER_EMAIL, { replace: true }),
	});

	const { workspaceId } = useParams<{ workspaceId: string }>();
	const [organisationIdFromSession] = useSessionStorage<string | undefined>('organisation');
	const { returnToOrganisationPicker } = useReturnToOrganisationPicker();

	const queryData = useDoDiscovery<DiscoveryResponse>(
		{ organisationId: organisationIdFromSession },
		{
			cacheTime: 0,
			retry: false,
			...queryOptions,
		}
	);

	const workspaces = resolveDiscoveryWorkspaces(queryData.data);
	const strategiesForWorkspace = getWorkspaceStrategies(queryData.data, workspaceId);

	const clearSessionAndGoToEmail = () => clearSession();

	const onGoBack = () => {
		// The identity cookie belongs to an email that has no access to this workspace.
		// Going to the workspace list would loop, so clear the session to let the user enter another email.
		if (strategiesForWorkspace.length === 0) {
			return clearSessionAndGoToEmail();
		}

		if (strategiesForWorkspace.length === 2) {
			return navigateTo(
				Step.SELECT_AUTHENTICATION_MULTI_STRATEGY_STEP.replace(':workspaceId', workspaceId ?? '')
			);
		}

		if (workspaces.length > 1) {
			return navigateTo(Step.SELECT_WORKSPACE);
		}

		returnToOrganisationPicker(clearSessionAndGoToEmail, queryData.data);
	};

	return {
		status: queryData.status,
		email: queryData.data?.email,
		workspace: strategiesForWorkspace[0],
		onGoBack,
		clearSessionAndGoToEmail,
	};
};
